<template>
  <div class="task-detail-page">
    <HeaderBar :userPower="userPower" :isShowBack="true" />
    <div class="task-detail-container">
      <div class="task-detail-header">
        <span class="task-title">
          {{ taskInfo.taskName || '' }}
        </span>
        <el-tag :type="getStatusType(getStatusText(taskInfo.statusCode))" size="default">
          {{ getStatusText(taskInfo.statusCode) }}
        </el-tag>
      </div>

      <div class="task-detail-content">
        <!-- 左侧：主角列表和素材列表 -->
        <div class="task-detail-left">
          <!-- 主角列表 -->
          <div class="character-section">
            <div class="section-header">
              <h3>
                基底视频列表
                <el-button type="primary" link style="margin-left: 12px;" @click="openTaskMaterialsDialog">任务素材</el-button>
              </h3>
              <div class="section-header-info">
                <el-text type="info" size="small">
                  目标数量：{{ targetBaseVideoCount }}，当前：{{ baseVideoList.length }}
                </el-text>
              </div>
            </div>
            <div class="character-list">
              <!-- 基底视频卡片列表 -->
              <div class="character-card-grid">
                <div v-for="(baseVideo, index) in baseVideoList" :key="baseVideo.id || index" class="character-card">
                  <div class="character-image-wrapper">
                    <LazyVideo :src="baseVideo.videoUrl" class="character-image" muted preload="metadata" />
                    <!-- 删除按钮悬浮在右上角 -->
                    <el-button class="character-delete-btn" circle type="danger" size="small"
                      @click.stop="handleRemoveBaseVideo(index)">
                      <el-icon>
                        <Delete />
                      </el-icon>
                    </el-button>
                    <!-- 标题信息悬浮在底部 -->
                    <div class="character-info">
                      <div class="character-title">{{ baseVideo.title }}</div>
                    </div>
                  </div>
                </div>

                <!-- 添加基底视频卡片 -->
                <div v-if="baseVideoList.length < targetBaseVideoCount" class="character-card add-character-card"
                  @click="openBaseVideoUploadDialog">
                  <div class="add-character-image">
                    <el-icon :size="32" color="#999">
                      <Plus />
                    </el-icon>
                    <el-text type="info" size="small">添加视频</el-text>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 素材列表 -->
          <div class="material-section">
            <div class="section-header">
              <h3>素材列表</h3>
            </div>
            <div class="material-list">
              <div v-for="(material, materialIndex) in materialList" :key="material.id || materialIndex"
                class="material-item">
                <div class="material-item-header">
                  <div class="material-item-title">
                    <div>
                      <el-text color="#fff" size="large">{{ material.templateName }} - {{ material.shotName }}</el-text>
                      <el-text type="info"> ({{ material.minDuration }}~{{ material.maxDuration }}s)</el-text>
                    </div>
                    <el-button v-if="material.exampleVideo && material.exampleVideo.url" type="info" size="small" text
                      :icon="Link" @click="handleCopyExampleUrl(material.exampleVideo.url)">示例链接</el-button>
                    <el-tooltip v-if="material.exampleVideo && material.exampleVideo.prompt && material.targetCount>0"
                      :content="material.exampleVideo.prompt" placement="top" effect="dark" :show-after="300">
                      <el-button type="info" size="small" text :icon="CopyDocument"
                        @click="handleCopyExamplePrompt(material.exampleVideo.prompt)">示例提示词</el-button>
                    </el-tooltip>
                    <el-text type="info" size="small">
                      当前:{{ material.videos.length }}/目标:{{ material.targetCount }}
                    </el-text>

                  </div>
                </div>

                <!-- 素材示例视频 -->
                <div class="material-videos-container">
                  <!-- 素材示例 -->
                  <div class="material-example-video">
                    <div class="material-video-item example-video"
                      @click="handlePreviewExampleVideo(material.exampleVideo)" v-if="material.exampleVideo">
                      <LazyVideo :src="material.exampleVideo.url" class="material-video" muted preload="metadata" />
                      <div class="material-video-overlay">
                        <el-icon :size="40" color="#fff">
                          <VideoPlay />
                        </el-icon>
                      </div>
                      <div class="material-example-label">素材示例</div>
                    </div>
                    <div v-else class="material-video-item example-video material-video-placeholder">
                      <el-icon :size="24">
                        <VideoCamera />
                      </el-icon>
                      <span>暂无示例视频</span>
                      <div class="material-example-label">素材示例</div>
                    </div>
                  </div>

                  <!-- 分割线 -->
                  <div class="material-videos-divider"></div>

                  <!-- 素材视频列表（横向排列） -->
                  <div class="material-videos-section">

                    <div class="material-videos-list">
                      <!-- 添加素材视频卡片 -->
                      <div v-if="material.videos.length < material.targetCount"
                        class="material-video-item add-material-video-card"
                        @click="handleBatchUploadMaterial(materialIndex)">
                        <div class="add-material-video-image">
                          <el-icon :size="24" color="#999">
                            <Plus />
                          </el-icon>
                          <el-text type="info" size="small">添加素材</el-text>
                        </div>
                      </div>
                      <div v-for="(video, videoIndex) in material.videos" :key="video.id || videoIndex"
                        class="material-video-item" :class="{ 'duration-insufficient': video.isDurationInsufficient }"
                        @click="handlePreviewMaterialVideo(video)">
                        <LazyVideo :src="video.url" class="material-video" muted preload="metadata" />
                        <div class="material-video-overlay">
                          <el-icon :size="40" color="#fff">
                            <VideoPlay />
                          </el-icon>
                        </div>
                        <!-- 时长不足红色蒙层 -->
                        <div v-if="video.isDurationInsufficient" class="duration-insufficient-overlay">
                          <div class="duration-insufficient-text">时长不足</div>
                        </div>
                        <!-- 视频时长显示 -->
                        <div v-if="video.duration" class="material-video-duration">
                          {{ video.duration.toFixed(2) }}s
                        </div>
                        <el-button class="material-video-delete-btn" circle type="danger" size="small"
                          @click.stop="handleRemoveMaterialVideo(materialIndex, videoIndex)">
                        <el-icon>
                          <Delete />
                        </el-icon>
                        </el-button>
                      </div>

                      <!-- material.targetCount=0 无需上传 -->
                      <div v-if="material.targetCount===0" style="text-align: center;margin-top: 35px;margin-left: 25px;">
                        <el-text type="info" size="small">采用主角口播，无需上传素材</el-text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧：结果列表 -->
        <div class="task-detail-right">
          <div class="result-section">
            <div class="section-header">
              <h3>结果列表</h3>
              <div v-if="resultVideoList.length !== 0 && taskInfo.remark !==''"  style="font-size: 12px;color: #999;">{{ taskInfo.remark}}</div>
              <div class="section-header-info">
                <el-text type="info" size="small">
                  数量：{{ resultVideoList.length }}/{{ targetVideoCount }}（实际/期望）
                </el-text>

              </div>
            </div>

            <div class="result-list-container">
              <div class="results-content">
                <div class="video-list-scrollable">
                  <!-- 空状态 -->
                  <div v-if="resultVideoList.length === 0 && taskInfo.remark ===''" class="empty-video-list">
                    <el-icon :size="80" color="#666">
                      <VideoCamera />
                    </el-icon>
                    <div class="empty-text">暂无视频</div>
                  </div>
                  <div v-else-if="resultVideoList.length === 0 && taskInfo.remark !==''" class="empty-video-list">
                    <el-icon :size="38" color="#666">
                      <Loading />
                    </el-icon>
                    <div class="empty-text">{{taskInfo.remark}}</div>
                  </div>

                  <!-- 实际内容 -->
                  <div v-else class="video-card-grid">
                    <el-card v-for="video in resultVideoList" :key="video.id" class="video-card"
                      :class="{ 'video-card-selected': selectedVideoIds.has(video.id) }">
                      <!-- 左上角选择框 -->
                      <div class="video-card-checkbox" @click.stop>
                        <el-checkbox :model-value="selectedVideoIds.has(video.id)" size="Large"
                          @change="toggleVideoSelect(video, $event)" />
                      </div>

                      <!-- 选中红色蒙层 -->
                      <div v-if="selectedVideoIds.has(video.id)" class="video-card-overlay"></div>

                      <!-- 缩略图区域 -->
                      <div class="video-card-thumbnail" :class="{ 'thumbnail-disabled': video.isGenerating }"
                        @click="!video.isGenerating && handleVideoClick(video)">
                        <!-- 生成中状态 -->
                        <div class="thumbnail-generating" v-if="video.isGenerating">
                          <el-icon :size="48" class="is-loading">
                            <Loading />
                          </el-icon>
                          <div class="generating-text">生成中...</div>
                        </div>
                        <!-- 无缩略图状态 -->
                        <div class="thumbnail-icon" v-else-if="!video.thumbnail">
                          <el-icon :size="48">
                            <VideoCamera />
                          </el-icon>
                        </div>
                        <!-- 有缩略图状态 -->
                        <img v-else :src="video.thumbnail" :alt="video.fileName" class="thumbnail-image" />
                        <!-- 播放按钮（仅在非生成中且有缩略图时显示） -->
                        <div class="thumbnail-play-button" v-if="!video.isGenerating && video.thumbnail">
                          <el-icon :size="50" color="#fff">
                            <VideoPlay />
                          </el-icon>
                        </div>
                        <!-- 生成时间（渐变透明浮层） -->
                        <div class="video-card-time">
                          <el-icon :size="11" style="margin-right: 3px; vertical-align: middle;">
                            <Clock />
                          </el-icon>
                          {{ formatDate(video.createDate) }}
                        </div>
                      </div>
                    </el-card>
                  </div>
                </div>
              </div>
            </div>

            <!-- 底部操作按钮
            1: '待分配',
            2: '待处理',
            3: '处理中',
            4: '已完成',
            5: '失败',
            -->
            <div class="result-actions">
              <el-button
                  v-if="taskInfo.statusCode === 3 || taskInfo.statusCode === 4"
                  type="danger"
                  style="margin-right: 8px;"
                  @click="handleBatchDeleteVideos"
                  :disabled="selectedVideoIds.size == 0"
                >
                删除选中 ({{ selectedVideoIds.size }})
              </el-button>
              <el-button
                  v-if="taskInfo.statusCode === 3 || taskInfo.statusCode === 4"
                  type="primary"
                  style="margin-right: 112px;"
                  @click="handleBatchDownloadVideos"
                  :disabled="selectedVideoIds.size == 0"
                  :loading="batchDownloading"
                >
                批量下载 ({{ selectedVideoIds.size }})
              </el-button>

              <el-button v-if="taskInfo.statusCode === 2  || taskInfo.statusCode === 5" type="danger" @click="handleRejectTask">
                拒绝任务
              </el-button>
              <el-button v-if="taskInfo.statusCode === 2 " type="primary" @click="handleStartProcess" :loading="processing">
                开始处理
              </el-button>
              <!-- <el-button v-if="taskInfo.statusCode === 4 || taskInfo.statusCode === 3" type="warning" @click="handleRejectUnqualified"
                :disabled="selectedUnqualifiedVideos.length === 0">
                不合格重做
              </el-button> -->
              <el-button v-if="taskInfo.statusCode >= 3" type="warning" @click="handleReExecuteTask" :loading="reExecuting" >
                重新执行
              </el-button>
              <el-button v-if="taskInfo.statusCode >= 3 && resultVideoList.length > 0" type="info" @click="handleSubmitResults" :loading="submitting" >
                提交结果
              </el-button>
              <el-button v-if="taskInfo.statusCode === 3" type="danger" @click="handleCompleteTask" :disabled="taskInfo.statusCode === 4">
                中断执行
              </el-button>
            </div>
          </div>
        </div>
      </div>

      <!-- 基底视频上传对话框 -->
      <el-dialog v-model="baseVideoUploadDialogVisible" title="上传基底视频" width="600px" :close-on-click-modal="false">
        <Upload ref="baseVideoUploadRef" v-model="uploadedBaseVideos" :accept="'.mp4,.avi,.mkv'"
          :batch-mode="true" :file-limit="targetBaseVideoCount" :multiple="true" upload-tip="支持批量上传，mp4/avi/mkv 视频，5G以内"
          @batch-upload-success="handleBaseVideoUploadSuccess" />
        <template #footer>
          <el-button @click="baseVideoUploadDialogVisible = false">取消</el-button>
        </template>
      </el-dialog>

      <!-- 素材批量上传对话框 -->
      <el-dialog v-model="materialUploadDialogVisible" :title="`上传素材视频 - ${currentMaterial?.templateName || ''}`"
        width="600px" :close-on-click-modal="false">
        <Upload ref="materialUploadRef" v-model="uploadedMaterialVideos" :accept="'.mp4,.avi'" :batch-mode="true"
          :multiple="true" :file-limit="999" upload-tip="支持批量上传，mp4/avi 视频，5G以内"
          @batch-upload-success="handleMaterialUploadSuccess" />
        <template #footer>
          <el-button @click="materialUploadDialogVisible = false">取消</el-button>
        </template>
      </el-dialog>

      <!-- 视频预览对话框（素材示例和结果视频共用） -->
      <el-dialog v-model="exampleVideoPreviewDialogVisible" title="视频预览" width="900px" :close-on-click-modal="false"
        align-center @close="handleVideoPreviewClose">
        <div class="example-video-preview-container">
          <video v-if="previewingExampleVideo" ref="previewVideoRef" :src="previewingExampleVideo.url" controls autoplay
            class="example-video-preview" />
        </div>
        <template #footer>
          <el-button @click="exampleVideoPreviewDialogVisible = false">关闭</el-button>
        </template>
      </el-dialog>

      <el-dialog v-model="taskMaterialsDialogVisible" title="任务素材" width="800px" :close-on-click-modal="false">
        <div class="task-materials-dialog-content">
          <div class="task-materials-meta" v-if="taskInfo.voiceInputType !== 'script'">
            <el-text type="warning">基底视频数量{{ targetBaseVideoCount || 0 }}个，特征：{{ taskInfo.characterDesc || '-' }}</el-text>
          </div>
          <template v-if="taskInfo.voiceInputType === 'audio'">
            <div class="task-materials-dialog-title">音频列表</div>
            <div v-if="taskInfo.audioFileList && taskInfo.audioFileList.length > 0" class="task-materials-list">
              <div v-for="(audio, index) in taskInfo.audioFileList" :key="index" class="task-materials-item">
                <div class="task-materials-item-title">{{ index + 1 }}. {{ audio.name || `音频${index + 1}` }}</div>
                <div class="task-materials-item-sub">{{ audio.duration ? `${audio.duration}s` : '时长未知' }}</div>
              </div>
            </div>
            <el-empty v-else description="暂无音频素材" />
          </template>

          <template v-else-if="taskInfo.voiceInputType === 'script'">
            <div class="task-materials-dialog-title">脚本列表（{{ taskInfo.videoScriptModelType || '-' }}）</div>
            <div v-if="publishedProjectScriptList.length > 0" class="task-materials-list">
              <div v-for="(script, index) in publishedProjectScriptList" :key="script.id || index" class="task-materials-item">
                <div class="task-materials-item-title">
                  <span>
                    脚本 {{ index + 1 }}
                    <template v-if="script.category || script.durationText !== '-'">
                      （
                      <template v-if="script.category">品类：{{ script.category }}</template>
                      <template v-if="script.category && script.durationText !== '-'">，</template>
                      <template v-if="script.durationText !== '-'">时长：{{ script.durationText }}</template>
                      ）
                    </template>
                  </span>
                  <el-button type="primary" link :icon="CopyDocument" @click="handleCopySingleScript(script.content)">复制脚本</el-button>
                </div>
                <div class="task-materials-item-content">{{ script.content || '' }}</div>
              </div>
            </div>
            <el-empty v-else description="暂无脚本素材" />
          </template>

          <template v-else>
            <div class="task-materials-dialog-title">文案列表</div>
            <div v-if="taskInfo.copyResults && taskInfo.copyResults.length > 0" class="task-materials-list">
              <div v-for="(copy, index) in taskInfo.copyResults" :key="index" class="task-materials-item">
                <div class="task-materials-item-title">文案 {{ index + 1 }}</div>
                <div class="task-materials-item-content">{{ copy.content || '' }}</div>
              </div>
            </div>
            <el-empty v-else description="暂无文案素材" />
          </template>
        </div>
        <template #footer>
          <el-button @click="taskMaterialsDialogVisible = false">关闭</el-button>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Delete, VideoCamera, VideoPlay, Document, Clock, Download, DocumentCopy, Loading, Link, CopyDocument } from '@element-plus/icons-vue'
import Upload from '@/apps/portal/components/Upload.vue'
import HeaderBar from '@/apps/portal/components/HeaderBar.vue'
import LazyVideo from '@/apps/portal/components/LazyVideo.vue'
import { getMyTaskInfo, saveTaskCharacters, saveTaskMaterials, startProcessTask, completeTask, rejectTask, updateMyTaskStatus, getTaskResultListByTaskId, updateTaskResultStatus, deleteTaskResult, reExecuteTask, submitTaskResults, getVideoScriptList } from '@/api/portal/pm'

const route = useRoute()
const router = useRouter()

const emit = defineEmits(['back'])

const props = defineProps({
  taskId: {
    type: [String, Number],
    default: null
  }
})

// 用户信息
const userPower = ref(0)
const getUserInfo = () => {
  const userInfo = localStorage.getItem('userInfo')
  if (userInfo) {
    const parsedInfo = JSON.parse(userInfo)
    userPower.value = parsedInfo.totalAmount || 0
  }
}

// 任务信息
const taskInfo = ref({
  id: null,
  taskName: '',
  status: '待分配',
  statusCode: 1,
  characterDesc: '',
  targetCharacterCount: 2,
  targetVideoCount: 10,
  remark: ''
})

// 获取任务状态类型
function getStatusType(status) {
  const statusMap = {
    '待分配': 'warning',
    '待处理': 'primary',
    '处理中': 'warning',
    '已完成': 'success',
    '失败': 'danger',
    '已取消': 'info'
  }
  return statusMap[status] || 'info'
}

// 获取任务状态文本
function getStatusText(statusCode) {
  const statusMap = {
    1: '待分配',
    2: '待处理',
    3: '处理中',
    4: '已完成',
    5: '失败',
    6: '已取消'
  }
  return statusMap[Number(statusCode)] || '未知'
}

// 目标数量
const targetBaseVideoCount = computed(() => {
  if (taskInfo.value.voiceInputType === 'audio') return taskInfo.value.audioFileList?.length || 0
  if (taskInfo.value.voiceInputType === 'script') return taskScriptList.value.length
  return taskInfo.value.copyResults?.length || 0
})
const targetVideoCount = computed(() => taskInfo.value.targetVideoCount || 0)

// 基底视频列表
const baseVideoList = ref([])

// 素材列表
const materialList = ref([])

// 结果视频列表
const resultVideoList = ref([])

// 批量选中的视频 id 集合
const selectedVideoIds = ref(new Set())

// 加载状态
const loading = ref(false)

// 选中的不合格视频列表（用于不合格重做功能）
const selectedUnqualifiedVideos = computed(() => {
  return resultVideoList.value.filter(v => v.isUnqualified === true)
})

// 对话框状态
const baseVideoUploadDialogVisible = ref(false)
const materialUploadDialogVisible = ref(false)
const exampleVideoPreviewDialogVisible = ref(false)
const taskMaterialsDialogVisible = ref(false)
const previewingExampleVideo = ref(null)
const previewVideoRef = ref(null)

const taskScriptList = computed(() => {
  const list = taskInfo.value.videoScriptList
  if (Array.isArray(list) && list.length > 0) {
    return list.map(item => ({
      content: item?.content == null ? '' : String(item.content)
    }))
  }
  if (taskInfo.value.videoScriptContent) {
    return [{ content: String(taskInfo.value.videoScriptContent) }]
  }
  return []
})
const publishedProjectScriptList = ref([])

function formatDurationText(value) {
  if (value === undefined || value === null || value === '') return '-'
  const num = Number(value)
  if (Number.isFinite(num)) {
    return `${num}秒`
  }
  return `${value}秒`
}

// 关闭视频预览时停止播放
function handleVideoPreviewClose() {
  if (previewVideoRef.value) {
    previewVideoRef.value.pause()
    previewVideoRef.value.currentTime = 0
  }
  previewingExampleVideo.value = null
}

async function openTaskMaterialsDialog() {
  await loadPublishedProjectScripts()
  taskMaterialsDialogVisible.value = true
}

async function loadPublishedProjectScripts() {
  const projectId = taskInfo.value.projectId
  if (!projectId) {
    publishedProjectScriptList.value = []
    return
  }
  try {
    const res = await getVideoScriptList(projectId)
    const list = res?.list || res?.data?.list || res?.data?.data?.list || []
    publishedProjectScriptList.value = (Array.isArray(list) ? list : [])
      .filter(item => Number(item?.isPublished) === 1)
      .map(item => ({
        id: item?.id,
        category: item?.category || '',
        content: item?.script == null ? '' : String(item.script),
        duration: item?.duration,
        durationText: formatDurationText(item?.duration)
      }))
  } catch (error) {
    publishedProjectScriptList.value = []
    ElMessage.error('加载已发布脚本失败')
  }
}

// 上传相关
const baseVideoUploadRef = ref(null)
const materialUploadRef = ref(null)
const uploadedBaseVideos = ref([])
const uploadedMaterialVideos = ref([])
const currentMaterialIndex = ref(-1)
const currentMaterial = computed(() => {
  if (currentMaterialIndex.value >= 0 && materialList.value[currentMaterialIndex.value]) {
    return materialList.value[currentMaterialIndex.value]
  }
  return null
})

const processing = ref(false)

function openBaseVideoUploadDialog() {
  if (baseVideoList.value.length >= targetBaseVideoCount.value) {
    ElMessage.warning(`基底视频数量已达到目标数量 ${targetBaseVideoCount.value}`)
    return
  }
  baseVideoUploadDialogVisible.value = true
  uploadedBaseVideos.value = []
}

// 基底视频上传成功
async function handleBaseVideoUploadSuccess(data) {
  if (data && data.batchList && data.batchList.length > 0) {
    const remainingSlots = targetBaseVideoCount.value - baseVideoList.value.length
    if (data.batchList.length > remainingSlots) {
      ElMessage.warning(`只能再添加 ${remainingSlots} 个基底视频，已自动限制`)
    }

    const toAdd = data.batchList.slice(0, remainingSlots)
    toAdd.forEach(item => {
      baseVideoList.value.push({
        id: Date.now() + Math.random(),
        videoUrl: item.url,
        title: item.name || '未命名基底视频'
      })
    })

    ElMessage.success(`已添加 ${toAdd.length} 个基底视频`)
    baseVideoUploadDialogVisible.value = false

    await saveBaseVideosToBackend()
  }
}

// 删除基底视频
async function handleRemoveBaseVideo(index) {
  baseVideoList.value.splice(index, 1)
  ElMessage.success('基底视频已删除')

  await saveBaseVideosToBackend()
}

// 保存基底视频列表到后端
async function saveBaseVideosToBackend() {
  if (!taskInfo.value.id) return

  try {
    const baseVideos = baseVideoList.value.map(item => ({
      id: item.id,
      videoUrl: item.videoUrl,
      url: item.videoUrl,
      title: item.title
    }))

    await saveTaskCharacters(taskInfo.value.id, baseVideos)
  } catch (error) {
    console.error('保存基底视频列表失败:', error)
  }
}

// 批量上传素材
function handleBatchUploadMaterial(materialIndex) {
  const material = materialList.value[materialIndex]
  if (!material) return

  if (material.videos.length >= material.targetCount) {
    ElMessage.warning(`素材数量已达到目标数量 ${material.targetCount}`)
    return
  }

  currentMaterialIndex.value = materialIndex
  materialUploadDialogVisible.value = true
  uploadedMaterialVideos.value = []
}

// 获取视频时长
function getVideoDuration(videoUrl) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.src = videoUrl

    video.addEventListener('loadedmetadata', () => {
      resolve(video.duration)
    })

    video.addEventListener('error', (error) => {
      console.error('加载视频失败:', error)
      reject(error)
    })
  })
}

// 素材上传成功
async function handleMaterialUploadSuccess(data) {
  if (currentMaterialIndex.value < 0 || !materialList.value[currentMaterialIndex.value]) {
    return
  }

  const material = materialList.value[currentMaterialIndex.value]
  if (data && data.batchList && data.batchList.length > 0) {
    // 检查数量限制
    const remainingSlots = material.targetCount - material.videos.length
    if (data.batchList.length > remainingSlots) {
      ElMessage.warning(`只能再添加 ${remainingSlots} 个素材，已自动限制`)
    }

    // 添加上传的视频并检测时长
    const toAdd = data.batchList.slice(0, remainingSlots)
    for (const item of toAdd) {
      try {
        // 获取视频时长
        const duration = await getVideoDuration(item.url)
        const isDurationInsufficient = duration < material.minDuration

        material.videos.push({
          id: Date.now() + Math.random(),
          url: item.url,
          fileName: item.name,
          size: item.size,
          duration: duration,
          isDurationInsufficient: isDurationInsufficient
        })

        if (isDurationInsufficient) {
          ElMessage.warning(`素材"${item.name}"时长不足（${duration.toFixed(1)}s < ${material.minDuration}s）`)
        }
      } catch (error) {
        console.error('获取视频时长失败:', error)
        // 即使获取时长失败，也添加视频，但标记为未知
        material.videos.push({
          id: Date.now() + Math.random(),
          url: item.url,
          fileName: item.name,
          size: item.size,
          duration: 0,
          isDurationInsufficient: false
        })
      }
    }

    ElMessage.success(`已添加 ${toAdd.length} 个素材`)
    materialUploadDialogVisible.value = false
    currentMaterialIndex.value = -1

    // 保存到后端
    await saveMaterialsToBackend()
  }
}

// 删除素材视频
async function handleRemoveMaterialVideo(materialIndex, videoIndex) {
  materialList.value[materialIndex].videos.splice(videoIndex, 1)
  ElMessage.success('素材已删除')

  // 保存到后端
  await saveMaterialsToBackend()
}

// 保存素材列表到后端
async function saveMaterialsToBackend() {
  if (!taskInfo.value.id) return

  try {
    const materials = materialList.value.map(mat => ({
      id: mat.id,
      templateName: mat.templateName,
      shotName: mat.shotName,
      targetCount: mat.targetCount,
      exampleVideo: mat.exampleVideo,
      videos: mat.videos
    }))

    await saveTaskMaterials(taskInfo.value.id, materials)
  } catch (error) {
    console.error('保存素材列表失败:', error)
  }
}

// 预览素材示例视频
function handlePreviewExampleVideo(exampleVideo) {
  if (!exampleVideo || !exampleVideo.url) {
    ElMessage.warning('示例视频不存在')
    return
  }
  previewingExampleVideo.value = exampleVideo
  exampleVideoPreviewDialogVisible.value = true
}

// 预览素材视频
function handlePreviewMaterialVideo(video) {
  if (!video || !video.url) {
    ElMessage.warning('视频不存在')
    return
  }
  previewingExampleVideo.value = video
  exampleVideoPreviewDialogVisible.value = true
}

// 复制示例视频链接
function handleCopyExampleUrl(url) {
  if (!url) {
    ElMessage.warning('链接不存在')
    return
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(url)
    ElMessage.success('复制链接成功')
  } else {
    ElMessage.error('复制失败')
  }
}

// 复制示例提示词
function handleCopyExamplePrompt(prompt) {
  if (!prompt) {
    ElMessage.warning('提示词不存在')
    return
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(prompt)
    ElMessage.success('复制提示词成功')
  } else {
    ElMessage.error('复制失败')
  }
}


// 格式化日期
function formatDate(dateString) {
  if (!dateString) return '-'
  try {
    const date = new Date(dateString)
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  } catch (error) {
    return dateString
  }
}

// 格式化文件大小
function formatFileSize(bytes) {
  if (!bytes) return '0B'
  if (bytes >= 1024 * 1024 * 1024) {
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + 'GB'
  } else if (bytes >= 1024 * 1024) {
    return (bytes / (1024 * 1024)).toFixed(2) + 'MB'
  } else if (bytes >= 1024) {
    return (bytes / 1024).toFixed(2) + 'KB'
  }
  return bytes + 'B'
}

// 视频合格/不合格状态变更
async function handleVideoQualityChange(video, checked) {
  try {
    // 更新本地状态
    video.isUnqualified = checked

    // 保存到数据库
    // status: 0-合格 1-不合格 2-已提交
    const status = checked ? 1 : 0
    const res = await updateTaskResultStatus(video.id, status)

    if (res && res.code === 0) {
      ElMessage.success(checked ? '已标记为不合格' : '已标记为合格')
    } else {
      // 如果保存失败，恢复原状态
      video.isUnqualified = !checked
      ElMessage.error(res.msg || '保存失败')
    }
  } catch (error) {
    // 如果保存失败，恢复原状态
    video.isUnqualified = !checked
    console.error('更新视频状态失败:', error)
    ElMessage.error('保存失败：' + (error.message || error))
  }
}

// 视频相关方法
function handleVideoClick(video) {
  if (video.fileUrl) {
    // 复用素材预览对话框
    previewingExampleVideo.value = { url: video.fileUrl }
    exampleVideoPreviewDialogVisible.value = true
  } else {
    ElMessage.warning('视频地址不存在')
  }
}

function handleDownloadVideo(video) {
  if (!video || !video.url) {
    ElMessage.warning('视频地址不存在')
    return
  }

  // 创建一个隐藏的 a 标签来触发下载
  const link = document.createElement('a')
  link.href = video.url
  link.download = video.fileName || '视频.mp4'
  link.target = '_blank'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  ElMessage.success('开始下载视频')
}

function handleCopyUrl(video) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(video.url || '')
    ElMessage.success('复制链接成功')
  } else {
    ElMessage.error('复制失败')
  }
}

async function handleCopySingleScript(content) {
  const text = String(content || '').trim()
  if (!text) {
    ElMessage.warning('脚本内容为空')
    return
  }
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text)
      ElMessage.success('复制脚本成功')
      return
    }
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
    ElMessage.success('复制脚本成功')
  } catch (e) {
    ElMessage.error('复制失败')
  }
}

async function handleDeleteVideo(video) {
  try {
    await ElMessageBox.confirm(
      `确定要删除视频"${video.fileName}"吗？`,
      '提示',
      { type: 'warning' }
    )
    // 从列表中删除
    const index = resultVideoList.value.findIndex(v => v.id === video.id)
    if (index > -1) {
      resultVideoList.value.splice(index, 1)
      ElMessage.success('删除成功')
    }
  } catch (e) {
    if (e !== 'cancel' && e !== 'close') {
      ElMessage.error('删除失败')
    }
  }
}

// 切换单个视频的选中状态
function toggleVideoSelect(video, checked) {
  const newSet = new Set(selectedVideoIds.value)
  if (checked) {
    newSet.add(video.id)
  } else {
    newSet.delete(video.id)
  }
  selectedVideoIds.value = newSet
}

// 批量删除选中的视频
async function handleBatchDeleteVideos() {
  const count = selectedVideoIds.value.size
  if (count === 0) return
  try {
    await ElMessageBox.confirm(
      `确定要删除选中的 ${count} 个视频吗？此操作不可恢复。`,
      '批量删除',
      { type: 'warning' }
    )

    const ids = [...selectedVideoIds.value]
    let successCount = 0
    let failCount = 0

    for (const id of ids) {
      try {
        const res = await deleteTaskResult(id)
        if (res && res.code === 0) {
          successCount++
          const index = resultVideoList.value.findIndex(v => v.id === id)
          if (index > -1) resultVideoList.value.splice(index, 1)
        } else {
          failCount++
        }
      } catch (e) {
        failCount++
      }
    }

    selectedVideoIds.value = new Set()

    if (failCount === 0) {
      ElMessage.success(`成功删除 ${successCount} 个视频`)
    } else {
      ElMessage.warning(`删除完成：成功 ${successCount} 个，失败 ${failCount} 个`)
    }
  } catch (e) {
    if (e !== 'cancel' && e !== 'close') {
      ElMessage.error('批量删除失败：' + (e.message || '未知错误'))
    }
  }
}

// 批量下载选中的视频
const batchDownloading = ref(false)
async function handleBatchDownloadVideos() {
  const count = selectedVideoIds.value.size
  if (count === 0) return
  if (count > 10) {
    ElMessage.warning('最多一次下载10个视频，请减少选中数量')
    return
  }

  const selectedVideos = resultVideoList.value.filter(v => selectedVideoIds.value.has(v.id))

  batchDownloading.value = true
  ElMessage.info(`正在准备下载 ${count} 个视频...`)

  try {
    for (let i = 0; i < selectedVideos.length; i++) {
      const video = selectedVideos[i]
      if (!video.fileUrl) {
        ElMessage.warning(`视频"${video.fileName}"地址不存在，已跳过`)
        continue
      }

      try {
        const response = await fetch(video.fileUrl, {
          method: 'GET',
          headers: { 'Accept': '*/*' }
        })

        if (!response.ok) {
          throw new Error(`${response.status} ${response.statusText}`)
        }

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = video.fileName || `视频${i + 1}.mp4`
        link.style.display = 'none'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)

        // 每个文件间隔500ms，避免浏览器拦截
        if (i < selectedVideos.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      } catch (err) {
        ElMessage.error(`下载"${video.fileName}"失败：${err.message || '未知错误'}`)
      }
    }

    ElMessage.success('批量下载完成')
  } finally {
    batchDownloading.value = false
  }
}
//接受任务
async function handleAcceptTask() {
  try {
    // 调用后端接口更新任务状态为已分配（2-已分配）
    const res = await updateMyTaskStatus(taskInfo.value.id, 2)

    if (res && res.code === 0) {
      taskInfo.value.status = '已分配'
      taskInfo.value.statusCode = 2
      ElMessage.success('任务已接受')
    } else {
      ElMessage.error(res.msg || '接受任务失败')
    }
  } catch (error) {
    console.error('接受任务失败:', error)
    ElMessage.error('接受任务失败：' + (error.message || error))
  }
}

// 拒绝任务
async function handleRejectTask() {
  try {
    await ElMessageBox.confirm(
      '确定要拒绝此任务吗？任务将还原到初始状态，并清空执行人，由发布人员重新分配。',
      '拒绝任务',
      {
        confirmButtonText: '确定拒绝',
        cancelButtonText: '取消',
        type: 'warning',
        confirmButtonClass: 'el-button--danger'
      }
    )

    // 调用后端接口拒绝任务
    const res = await rejectTask(taskInfo.value.id)

    if (res && res.code === 0) {
      ElMessage.success('任务已拒绝，已还原到初始状态')

      // 返回到任务列表页面
      setTimeout(() => {
        // 判断是否在 Home.vue 内部使用（通过检查是否有 back 事件监听器）
        // 如果当前路由是 /task/detail，说明是独立路由模式，需要跳转
        if (route.path === '/task/detail') {
          // 独立路由模式（新标签页打开），跳转到首页我的任务
          router.push({ path: '/home', query: { view: 'myTasks' } })
        } else {
          // 嵌入模式（Home.vue 内部切换视图），触发 back 事件
          emit('back')
        }
      }, 1500)
    } else {
      ElMessage.error(res.msg || '拒绝任务失败')
    }
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('拒绝任务失败:', error)
      ElMessage.error('拒绝任务失败：' + (error.message || error))
    }
  }
}

// 开始处理
async function handleStartProcess() {

  processing.value = true

  // 验证前置条件
  if (baseVideoList.value.length < targetBaseVideoCount.value) {
    ElMessage.warning(`基底视频数量不足，需要 ${targetBaseVideoCount.value} 个，当前 ${baseVideoList.value.length} 个`)
    return
  }

  // 检查素材
  let materialIncomplete = false
  let hasDurationInsufficient = false
  for (const material of materialList.value) {
    if (material.videos.length < material.targetCount) {
      materialIncomplete = true
      break
    }

    // 检查素材时长是否符合要求
    for (const video of material.videos) {
      if (video.isDurationInsufficient) {
        hasDurationInsufficient = true
        break
      }
    }

    if (hasDurationInsufficient) {
      break
    }
  }

  if (materialIncomplete) {
    ElMessage.warning('部分模板的素材数量不足，请补充素材')
    return
  }

  if (hasDurationInsufficient) {
    ElMessage.warning('部分素材时长不足，请更换符合时长要求的素材')
    return
  }

  // 检查口播音频相关数据
  const hasCopyResults = taskInfo.value.copyResults && taskInfo.value.copyResults.length > 0
  const hasAudioFileList = taskInfo.value.audioFileList && taskInfo.value.audioFileList.length > 0
  const hasSelectedVoices = taskInfo.value.selectedVoices && taskInfo.value.selectedVoices.length > 0

  // 验证逻辑：
  // 1. 如果有上传的音频文件，直接通过（不需要文案和音色）
  // 2. 如果没有上传音频，必须同时有文案和音色（后端会生成TTS音频）

  if (!hasAudioFileList) {
    // 没有上传音频，检查是否有文案和音色
    if (!hasCopyResults) {
      ElMessage.warning('请联系管理员，提供文案或上传口播音频')
      return
    }

    if (!hasSelectedVoices) {
      ElMessage.warning('请联系管理员，请选择音色，或直接上传口播音频')
      return
    }
  }

  //验证文件的可访问性
  // 校验口播音频可访问性
  if (taskInfo.value.audioFileList && taskInfo.value.audioFileList.length > 0) {
    for (const audio of taskInfo.value.audioFileList) {
      const accessible = await checkUrlAccessible(audio.url, 'audio')
      if (!accessible) {
        ElMessage.warning(`口播音频「${audio.name || audio.url}」无法访问，请联系管理员`)
        return
      }
    }
  }

  // 校验基底视频可访问性
  for (const baseVideo of baseVideoList.value) {
    const accessible = await checkUrlAccessible(baseVideo.videoUrl, 'video')
    if (!accessible) {
      ElMessage.warning(`基底视频「${baseVideo.title || baseVideo.videoUrl}」无法访问，请重新上传`)
      return
    }
  }

  // 校验素材片段可访问性
  for (const material of materialList.value) {
    for (const video of material.videos) {
      const accessible = await checkUrlAccessible(video.url, 'video')
      if (!accessible) {
        ElMessage.warning(`素材「${video.fileName || video.url}」无法访问，请重新上传`)
        return
      }
    }
  }

  try {

    taskInfo.remark = '开始处理...'

    // 调用后端接口，将任务状态改为处理中（status=3）
    // 后端定时任务会自动处理TTS、单人口播视频生成和视频合成
    const res = await startProcessTask(taskInfo.value.id)

    if (res && res.code === 0) {
      // 更新任务状态
      taskInfo.value.status = '处理中'
      taskInfo.value.statusCode = 3

      ElMessage.success('任务已提交，正在后台处理中...')
      ElMessage.info('视频生成需要一些时间，请稍后刷新页面查看结果')

      // 可以启动一个轮询来检查任务状态
      startPollingTaskStatus()
    } else {
      ElMessage.error(res.msg || '提交任务失败')
    }

  } catch (error) {
    console.error('提交任务失败:', error)
    ElMessage.error('提交任务失败：' + (error.message || error))
  } finally {
    processing.value = false
  }
}

// 轮询任务状态（已禁用自动刷新）
let pollingTimer = null
function startPollingTaskStatus() {
  // 自动刷新已禁用，用户可以手动刷新页面查看最新状态
  console.log('自动刷新已禁用，请手动刷新页面查看最新状态')
}

// 组件卸载时清除定时器（已禁用自动刷新）
onUnmounted(() => {
  // 自动刷新已禁用
})

// 不合格重做
async function handleRejectUnqualified() {
  const unqualifiedVideos = selectedUnqualifiedVideos.value
  if (unqualifiedVideos.length === 0) {
    ElMessage.warning('请先标记不合格的视频')
    return
  }

  try {
    await ElMessageBox.confirm(
      `确定要重新生成 ${unqualifiedVideos.length} 个不合格视频吗？`,
      '确认操作',
      { type: 'warning' }
    )

    // 标记为生成中并清空缩略图，保持不合格标识
    const selectedIds = unqualifiedVideos.map(v => v.id)
    resultVideoList.value.forEach(video => {
      if (selectedIds.includes(video.id)) {
        video.isGenerating = true
        video.thumbnail = ''
        // 保持不合格标识，生成完成后再清空
      }
    })

    ElMessage.info('正在重新生成视频...')

    // TODO: 调用API重新生成
    await new Promise(resolve => setTimeout(resolve, 2000))

    // 更新现有视频数据（模拟生成完成）
    resultVideoList.value.forEach((video, index) => {
      if (video.isGenerating) {
        // 更新视频信息
        video.fileName = `重新生成的视频${index + 1}.mp4`
        video.fileSize = 1024 * 1024 * 50
        video.createDate = new Date().toISOString()
        video.thumbnail = '' // 这里应该是新的缩略图URL，暂时留空
        video.fileUrl = '' // 这里应该是新的视频URL
        video.isGenerating = false
        video.isUnqualified = false
      }
    })

    ElMessage.success('视频重新生成完成')
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('操作失败：' + (error.message || error))
      // 如果出错，取消生成中状态
      const selectedIds = unqualifiedVideos.map(v => v.id)
      resultVideoList.value.forEach(video => {
        if (selectedIds.includes(video.id) && video.isGenerating) {
          video.isGenerating = false
        }
      })
    }
  }
}

// 重新执行任务（状态改为3，删除 isMerging 参数）
const reExecuting = ref(false)
async function handleReExecuteTask() {
  try {
    await ElMessageBox.confirm(
      '确定要重新执行此任务吗？任务状态将变为「处理中」，并清除合并标记。',
      '重新执行',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    reExecuting.value = true

    const res = await reExecuteTask(taskInfo.value.id)
    if (res && res.code === 0) {
      taskInfo.value.statusCode = 3
      ElMessage.success('任务已重新执行，状态已变为处理中')
      startPollingTaskStatus()
    } else {
      ElMessage.error(res?.msg || '重新执行失败')
    }
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('重新执行任务失败:', error)
      ElMessage.error('重新执行失败：' + (error.message || error))
    }
  } finally {
    reExecuting.value = false
  }
}

// 提交任务结果（将所有结果状态改为2-已提交）
const submitting = ref(false)
async function handleSubmitResults() {
  if (resultVideoList.value.length === 0) {
    ElMessage.warning('暂无任务结果可提交')
    return
  }

  try {
    await ElMessageBox.confirm(
      `确定要提交全部 ${resultVideoList.value.length} 个任务结果吗？提交后状态将变为「已提交」。`,
      '提交结果',
      {
        confirmButtonText: '确定提交',
        cancelButtonText: '取消',
        type: 'warning'
      }
    )

    submitting.value = true
    const res = await submitTaskResults(taskInfo.value.id)
    if (res && res.code === 0) {
      ElMessage.success(res.message || `已成功提交 ${res.count || ''} 个任务结果`)
    } else {
      ElMessage.error(res?.msg || '提交失败')
    }
  } catch (error) {
    if (error !== 'cancel' && error !== 'close') {
      console.error('提交结果失败:', error)
      ElMessage.error('提交失败：' + (error.message || error))
    }
  } finally {
    submitting.value = false
  }
}

// 任务完成
async function handleCompleteTask() {
  if (resultVideoList.value.length < targetVideoCount.value) {
    ElMessage.warning(`视频数量不足，需要 ${targetVideoCount.value} 个，当前 ${resultVideoList.value.length} 个`)
    return
  }

  try {
    await ElMessageBox.confirm(
      '确定要完成任务吗？',
      '确认操作',
      { type: 'warning' }
    )

    // 调用API完成任务
    const res = await completeTask(taskInfo.value.id)
    if (res && res.code === 0) {
      taskInfo.value.status = '已完成'
      taskInfo.value.statusCode = 4
      ElMessage.success('任务已完成')
    } else {
      ElMessage.error(res.msg || '操作失败')
    }
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('操作失败：' + (error.message || error))
    }
  }
}

// 获取任务详情
async function fetchTaskDetail() {
  const taskId = props.taskId || route.query.taskId
  if (!taskId) {
    ElMessage.error('任务ID不存在')
    return
  }

  try {
    loading.value = true
    const res = await getMyTaskInfo(taskId)
    if (res && res.code === 0) {
      const task = res.task
      if (!task) {
        ElMessage.error('任务数据为空，请检查任务ID是否正确')
        return
      }
      const taskDetails = res.taskDetails || {}

      // 设置任务基本信息
      taskInfo.value = {
        id: task.id,
        projectId: task.projectId || task.project_id || '',
        taskName: task.taskName || task.task_name,
        status: getStatusText(Number(task.status)),
        statusCode: Number(task.status),
        remark: task.remark || '',
        characterDesc: taskDetails.characterDesc || '',
        targetCharacterCount: taskDetails.characterCount || 2,
        targetVideoCount: calculateTargetVideoCount(taskDetails),
        // 添加缺失的字段
        copyResults: taskDetails.copyResults || [],
        audioFileList: taskDetails.audioFileList || [],
        voiceInputType: taskDetails.voiceInputType || '',
        videoScriptModelType: taskDetails.videoScriptModelType || '',
        videoScriptContent: taskDetails.videoScriptContent || '',
        videoScriptList: taskDetails.videoScriptList || [],
        selectedVoices: taskDetails.selectedVoices || [],
        templates: taskDetails.templates || [],
        selectedTemplateQuantities: taskDetails.selectedTemplateQuantities || {},
        // 视频参数
        videoDirection: taskDetails.videoDirection || 'vertical',
        videoResolution: taskDetails.videoResolution || '720p',
        // 字幕参数
        subtitleEnabled: taskDetails.subtitleEnabled ?? false,
        subtitlePosTop: taskDetails.subtitlePosTop ?? 65,
        subtitlePosBottom: taskDetails.subtitlePosBottom ?? 75,
        fontSize: taskDetails.fontSize ?? 32,
        subtitleBgColor: taskDetails.subtitleBgColor || 'rgba(0, 0, 0, 0)',
        subtitleTextColor: taskDetails.subtitleTextColor || 'rgba(255, 255, 255, 1)'
      }


      // 设置基底视频列表（兼容旧字段 characters）
      const rawBaseVideos = taskDetails.baseVideoList || taskDetails.characters || []
      baseVideoList.value = (rawBaseVideos || []).map((item, idx) => ({
        id: item.id || `${Date.now()}_${idx}`,
        videoUrl: item.videoUrl || item.imageUrl || item.url || '',
        title: item.title || `基底视频${idx + 1}`
      })).filter(item => item.videoUrl)

      // 设置素材列表：始终从 templates 重新计算各字段（因 materialLimit 可能改变），只保留已保存 materials 中的 videos 数据
      if (taskDetails.templates && taskDetails.templates.length > 0) {
        // 构建已保存 materials 的 videos 映射，key 为素材 id
        const savedVideosMap = {}
        if (taskDetails.materials && taskDetails.materials.length > 0) {
          taskDetails.materials.forEach(mat => {
            savedVideosMap[mat.id] = mat.videos || []
          })
        }

        materialList.value = []
        taskDetails.templates.forEach(template => {
          if (template.shots && template.shots.length > 0) {
            template.shots.forEach(shot => {
              const generateQuantity = taskDetails.selectedTemplateQuantities?.[template.id] || template.generateQuantity || 1
              const materialLimit = taskDetails.materialLimit || 1
              let targetCount = 0
              if (shot.insertMode !== 'narration') {
                targetCount = Math.ceil(generateQuantity / materialLimit)
              }
              const matId = `${template.id}_${shot.id}`
              materialList.value.push({
                id: matId,
                templateName: template.name,
                shotName: shot.name,
                minDuration: shot.minDuration,
                maxDuration: shot.maxDuration,
                targetCount: targetCount,
                exampleVideo: shot.materialExample ? {
                  id: shot.materialExample.id,
                  url: shot.materialExample.fileUrl,
                  fileName: shot.materialExample.fileName,
                  prompt: shot.materialExample.prompt
                } : null,
                videos: savedVideosMap[matId] || []
              })
            })
          }
        })
      } else {
        // 回退：templates 不存在时直接使用已保存的素材列表
        ElMessage.error('没有模板数据')
        return
      }

      // ========== 从 pm_projects_tasks_result 表读取结果视频列表 ==========
      await fetchTaskResultVideos(taskId)

      // ========== 如果任务状态是处理中（status=3），自动启动轮询 ==========
      if (task.status === 3) {
        console.log('任务处于处理中状态，启动轮询')
        startPollingTaskStatus()
      }

    } else {
      ElMessage.error(res.msg || '获取任务详情失败')
    }
  } catch (error) {
    console.error('[TaskDetail] fetchTaskDetail error:', error)
    ElMessage.error('获取任务详情失败：' + (error?.message || error))
    console.error(error)
  } finally {
    loading.value = false
  }
}

// 从 pm_projects_tasks_result 表获取结果视频列表
async function fetchTaskResultVideos(taskId) {
  try {
    const res = await getTaskResultListByTaskId(taskId)
    if (res && res.code === 0) {
      const resultList = res.list || []

      // 转换为前端需要的格式
      resultVideoList.value = resultList.map(item => {
        // 解析 resultContent JSON
        let resultContent = {}
        try {
          if (item.resultContent) {
            resultContent = JSON.parse(item.resultContent)
          }
        } catch (e) {
          console.error('解析 resultContent 失败:', e)
        }

        return {
          id: item.id,
          fileName: item.fileName || resultContent.fileName || '未命名视频',
          fileUrl: item.fileUrl || resultContent.fileUrl || '',
          thumbnail: resultContent.thumbnail || '',
          fileSize: resultContent.fileSize || 0,
          createDate: item.createDate || item.completeDate || new Date().toISOString(),
          // 从数据库读取不合格状态：status状态 0-合格 1-不合格 2-已提交
          isUnqualified: item.status === 1,
          isGenerating: false,
          templateName: resultContent.templateName || '',
          baseVideoFileName: resultContent.baseVideoFileName || '',
          duration: resultContent.duration || 0
        }
      })

      console.log('从结果表加载视频列表，数量:', resultVideoList.value.length)
    } else {
      console.warn('获取任务结果列表失败:', res.msg)
      // 如果从结果表获取失败，尝试从 taskDetails 获取（兼容旧数据）
      resultVideoList.value = []
    }
  } catch (error) {
    console.error('获取任务结果列表异常:', error)
    resultVideoList.value = []
  }
}

// 计算目标视频数量
function calculateTargetVideoCount(taskDetails) {
  if (!taskDetails.templates || taskDetails.templates.length === 0) {
    return 0 // 默认值
  }

  let total = 0
  taskDetails.templates.forEach(template => {
    const quantity = taskDetails.selectedTemplateQuantities?.[template.id] || 1
    total += quantity
  })

  return total
}

// 监听taskId变化
watch(() => props.taskId || route.query.taskId, (newTaskId) => {
  if (newTaskId) {
    fetchTaskDetail()
  }
}, { immediate: true })

onMounted(() => {
  document.title = '任务详情'
  getUserInfo()
})

// 检测 URL 是否可有效访问（通用函数）
// type: 'audio' | 'video' | 'image'
const checkUrlAccessible = (url, type = 'video') => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(true)
      return
    }
    if (type === 'image') {
      const img = new Image()
      img.onload = () => resolve(true)
      img.onerror = () => resolve(false)
      img.src = url
    } else if (type === 'audio') {
      const audio = document.createElement('audio')
      audio.oncanplaythrough = () => resolve(true)
      audio.onerror = () => resolve(false)
      audio.preload = 'metadata'
      audio.src = url
    } else {
      const vid = document.createElement('video')
      vid.oncanplaythrough = () => resolve(true)
      vid.onerror = () => resolve(false)
      vid.preload = 'metadata'
      vid.src = url
    }
  })
}
</script>

<style scoped>
.task-detail-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #FFFFFF;
}

.task-detail-container {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  background: #FFFFFF;
}

.task-detail-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 6px 24px;
  background: var(--bg-card);
  border-bottom: 1px solid var(--border-light);
}

.task-title {
  color: var(--text-primary);
  font-size: 20px;
  font-weight: 600;
}

.task-remark {
  color: var(--el-color-primary);
  font-size: 13px;
  margin-left: 8px;
  padding: 4px 12px;
  background: rgba(79, 70, 229, 0.1);
  border-radius: 4px;
  border: 1px solid rgba(79, 70, 229, 0.3);
}



.task-detail-content {
  display: flex;
  flex: 1;
  overflow: hidden;
}

.task-detail-left {
  width: 50%;
  border-right: 1px solid var(--border-light);
  display: flex;
  flex-direction: column;
  padding: 16px;
}

.task-detail-right {
  width: 50%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 通用section样式 */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header h3 {
  margin: 0;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 600;
}

.section-header-info {
  display: flex;
  align-items: center;
}

/* 主角列表样式 */
.character-section {
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid var(--border-light);
}

.character-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.character-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 12px;
}

.character-card {
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.character-card:hover {
  border-color: var(--el-color-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.add-character-card {
  cursor: pointer;
  border: 2px dashed var(--border-light);
  background: #FFFFFF;
}

.add-character-card:hover {
  border-color: var(--el-color-primary);
  background: rgba(79, 70, 229, 0.05);
}

.add-character-image {
  width: 100%;
  height: 90px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
}

.character-image-wrapper {
  position: relative;
  width: 100%;
  height: 90px;
  overflow: hidden;
}

.character-image {
  width: 100%;
  height: 100%;
  background: #FFFFFF;
}

.character-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  color: var(--text-muted);
}

/* 删除按钮悬浮在右上角 */
.character-delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  backdrop-filter: blur(4px);
}

.character-delete-btn:hover {
  background: rgba(245, 101, 101, 0.8);
}

/* 标题信息悬浮在底部 */
.character-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2px 6px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.4), transparent);
  backdrop-filter: blur(4px);
  z-index: 5;
}

.character-title {
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

/* 素材列表样式 */
.material-section {
  flex: 1;
}

.material-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
  padding: 2px 0;
  height: calc(100vh - 330px);
}

.material-item {
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: 8px;
  padding: 8px 16px;
}

.material-item-header {
  margin-bottom: 2px;
}

.material-item-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 8px 0;
  color: var(--text-primary);
  font-size: 15px;
  font-weight: 600;
}

.material-videos-container {
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 12px;
  margin-bottom: 2px;
}

.material-example-video {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.material-videos-divider {
  width: 0;
  height: 80px;
  margin: 0 2px;
  align-self: center;
  flex-shrink: 0;
  border-left: 1px solid #D1D5DB;
}

.material-example-label {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 6px 8px;
  color: #5d5c5c;
  font-size: 12px;
  background: linear-gradient(to top, rgba(240, 240, 240, 0.78) 0%, rgba(255, 255, 255, 0.35) 60%, transparent 100%);
  backdrop-filter: blur(4px);
  z-index: 5;
  text-align: center;
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.7);
}

.material-videos-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.material-videos-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.material-videos-label {
  color: var(--text-secondary);
  font-size: 13px;
}

.material-videos-list {
  display: flex;
  align-items: center;
  gap: 12px;
  overflow-x: auto;
  padding-top: 2px;
}

.material-video-item {
  position: relative;
  flex-shrink: 0;
  width: 100px;
  height: 100px;
  aspect-ratio: 1 / 1;
  background: #FFFFFF;
  border: 1px solid var(--border-light);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.material-video-item:hover {
  border-color: var(--el-color-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.material-video-item.example-video {
  width: 100px;
  height: 100px;
  aspect-ratio: 1 / 1;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.material-video-item.example-video:hover {
  border-color: var(--el-color-primary);
}

.material-video-item.example-video .material-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.material-video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 2;
}

.material-video-item.example-video:hover .material-video-overlay {
  opacity: 1;
}

.material-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  background: #FFFFFF;
}

.material-video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  color: var(--text-muted);
  gap: 8px;
  font-size: 12px;
}

/* 素材示例视频预览对话框样式 */
.example-video-preview-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  background: #000;
}

.example-video-preview {
  width: 100%;
  max-width: 800px;
  max-height: 600px;
}

.material-video-delete-btn {
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 10;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  backdrop-filter: blur(4px);
}

.material-video-delete-btn:hover {
  background: rgba(245, 101, 101, 0.8);
}

.material-video-item:hover .material-video-overlay {
  opacity: 1;
}

/* 时长不足样式 */
.material-video-item.duration-insufficient {
  border-color: #f56565;
}

.duration-insufficient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(105, 5, 5, 0.7);
  z-index: 5;
  pointer-events: none;
}

.duration-insufficient-text {
  color: #FFFFFF;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
}

/* 视频时长显示 */
.material-video-duration {
  position: absolute;
  bottom: 0;
  left: 0;
  padding: 4px 8px;
  margin: 2px;
  color: #FFFFFF;
  font-size: 11px;
  font-weight: 500;
  background: rgba(0, 0, 0, 0.65);
  border-radius: 8px;
  z-index: 6;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

.add-material-video-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed var(--border-light);
  background: #FFFFFF;
  cursor: pointer;
}

.add-material-video-card:hover {
  border-color: var(--el-color-primary);
  background: rgba(79, 70, 229, 0.05);
}

.add-material-video-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
  gap: 6px;
}

/* 结果列表样式 */
.result-section {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}

.result-list-container {
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--border-light);
}

/* 对话框样式 */
.character-preset-container {
  display: flex;
  gap: 20px;
  height: 500px;
}

.character-preset-grid {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  overflow-y: auto;
  padding-right: 8px;
}

.character-preview-panel {
  width: 320px;
  flex-shrink: 0;
  background: var(--bg-app);
  border: 1px solid var(--border-light);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.preview-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.preview-title {
  padding: 16px;
  text-align: center;
  color: var(--el-color-primary);
  font-size: 16px;
  font-weight: 500;
  border-bottom: 1px solid var(--border-light);
  background: #FFFFFF;
}

.preview-image {
  flex: 1;
  width: 100%;
  height: calc(100% - 56px);
}

.preview-image :deep(.el-image__inner) {
  object-fit: contain;
}

.preview-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: var(--text-muted);
}

.character-preset-card {
  background: var(--bg-card);
  border: 2px solid var(--border-light);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  height: 180px;
  display: flex;
  flex-direction: column;
}

.character-preset-card:hover {
  border-color: var(--el-color-primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
}

.character-preset-card.selected {
  border-color: var(--el-color-primary);
  background: rgba(79, 70, 229, 0.1);
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.2);
}

.preset-image {
  width: 100%;
  height: 140px;
  flex-shrink: 0;
  background: #FFFFFF;
}

.preset-title {
  padding: 8px;
  text-align: center;
  color: var(--text-primary);
  font-size: 13px;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-new-character-card {
  border: 2px dashed var(--border-light);
  background: #FFFFFF;
  cursor: pointer;
}

.add-new-character-card:hover {
  border-color: var(--el-color-primary);
  background: rgba(79, 70, 229, 0.05);
}

.add-new-character-image {
  width: 100%;
  height: 140px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #FFFFFF;
}

/* 结果列表样式 */
.result-list-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.results-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.video-list-scrollable {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  padding: 16px;
}

.video-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
  padding: 16px 0;
}

.video-card {
  position: relative;
  transition: all 0.3s ease;
  background: #FFFFFF;
  border: 1px solid var(--border-light);
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.video-card-unqualified {
  position: relative;
}

.video-card-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(79, 70, 229, 0.18);
  z-index: 5;
  pointer-events: none;
  border-radius: 6px;
}

.video-card-checkbox {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 10;
}

.video-card-checkbox :deep(.el-checkbox__inner) {
  width: 18px;
  height: 18px;
  border-radius: 4px;
  border: 2px solid rgba(255,255,255,0.8);
  background: rgba(255, 255, 255, 0.92);
  border: 2px solid rgba(229, 231, 235, 0.95);
}

.video-card-checkbox :deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: var(--el-color-primary);
  border-color: var(--el-color-primary);
}


.video-card-time {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 18px 8px 5px;
  color: var(--bg-card);
  font-size: 11px;
  background: linear-gradient(to top, rgba(17,24,39,0.78) 0%, rgba(17,24,39,0.35) 60%, transparent 100%);
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 1px 2px rgba(0,0,0,0.8);
  pointer-events: none;
}

.video-card-selected {
  box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.35) !important;
}

.video-card :deep(.el-card__body) {
  padding: 0;
  background: transparent;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  border-color: var(--el-color-primary);
}

.video-card-thumbnail {
  width: 100%;
  height: 180px;
  background: var(--bg-app);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;
  cursor: pointer;
}

.video-card-thumbnail.thumbnail-disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
}

.thumbnail-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: var(--text-muted);
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
}

.thumbnail-generating {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  color: var(--el-color-primary);
}

.thumbnail-generating .is-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.generating-text {
  margin-top: 12px;
  color: var(--el-color-primary);
  font-size: 14px;
  font-weight: 500;
}

.thumbnail-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  opacity: 0;
}

.video-card-thumbnail:hover .thumbnail-play-button {
  opacity: 1;
}



/* 视频预览对话框样式 */
.video-preview-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.video-preview {
  width: 100%;
  max-height: 500px;
  background: #000;
  border-radius: 8px;
}

.video-preview-info {
  margin-top: 8px;
}

.video-preview-info :deep(.el-descriptions) {
  background: var(--bg-app);
}

.video-preview-info :deep(.el-descriptions__label) {
  background: var(--bg-card);
  color: var(--text-secondary);
}

.video-preview-info :deep(.el-descriptions__content) {
  background: var(--bg-app);
  color: var(--text-primary);
}

/* 视频预览对话框样式 */
.video-preview-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.video-preview {
  width: 100%;
  max-height: 500px;
  background: #000;
  border-radius: 8px;
}

.video-preview-info {
  margin-top: 8px;
}

.video-preview-info :deep(.el-descriptions) {
  background: var(--bg-app);
}

.video-preview-info :deep(.el-descriptions__label) {
  background: var(--bg-card);
  color: var(--text-secondary);
}

.video-preview-info :deep(.el-descriptions__content) {
  background: var(--bg-app);
  color: var(--text-primary);
}

.empty-video-list {
  color: var(--text-secondary);
  padding: 80px 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.empty-text {
  margin-top: 16px;
  font-size: 16px;
  color: var(--text-secondary);
}
.task-materials-dialog-content {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 6px;
}

.task-materials-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 10px;
}

.task-materials-dialog-title {
  margin-bottom: 10px;
  color: var(--text-primary, #1F2937);
  font-size: 14px;
  font-weight: 600;
}

.task-materials-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.task-materials-item {
  padding: 10px 12px;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  background: #F9FAFB;
}

.task-materials-item-title {
  margin-bottom: 6px;
  color: var(--text-primary, #1F2937);
  font-size: 13px;
}

.task-materials-item-sub {
  color: var(--text-secondary, #6B7280);
  font-size: 12px;
}

.task-materials-item-content {
  color: #374151;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
}
</style>
